import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, Pencil, Building2, Laptop, Lock, Smartphone, Star, Trash2 } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import {
  ASSET_CATEGORIES,
  getHardwareCatalogue, saveHardwareAccessory, saveHardwareDevice, saveHardwareGroup,
  type HwCatalogueAccessory, type HwCatalogueDevice, type HwCatalogueGroup,
  type HwDeviceInput, type HwGroupInput,
} from '@/api/hardwareCatalogue';
import { getLocations } from '@/api/locations';
import { formatMoneyExact } from '@/lib/dateFormat';
import { PurchaseNoticePanel } from './PurchaseNoticePanel';
import { StandingProjectPanel } from './StandingProjectPanel';
import { createStockItem, listAccessoryLinks, listStockItems, putStockItemDevices } from '@/api/stock';
import type { AccessoryLink, StockItem } from '@/types/oscar';
import { AccessoryStockBadge, AccessoryStockPanel, DeviceStockPanel, StockBadge } from './DeviceStockPanel';
import { accessoryLinesFor } from './accessoryStock';
import { orphanLines } from './deviceStock';

/**
 * What to hold on the shelf for a device being created, if anything.
 *
 * ⛔ WHY THIS IS HERE AND NOT ONLY ON THE STOCK PAGE. Anthony, 01/10/2026:
 * "it seems we are duplicating names. name of stock item, name of hardware."
 * He is right about the typing and wrong about the storage — see §15 — so the
 * fix is to type it ONCE, here, at the moment the device is created.
 *
 * ⚠ OPTIONAL, AND OFF. Most devices are not stocked: an iPhone comes from the
 * telco, a label printer is bought per request. Defaulting this on would create
 * an empty stock line for every one of them.
 */
interface StockDraft {
  hold: boolean;
  locationId: number | null;
  qty: number;
  serialised: boolean;
}

/**
 * Hardware catalogue — the devices and accessories the Hardware and Mobile
 * Device Request form offers.
 *
 * ⛔ MOVED HERE FROM manage ON 30/09/2026 (Stage 1, design §13). It used to be
 * at manage's /settings/hardware. Three things happen to a device option — we
 * publish it, we decide who may ask for it, and we issue it — and the third was
 * already Oscar's. The other two followed.
 *
 * ⛔ THE RULE THIS PAGE EXISTS TO MAKE SAFE. A submission stores an option's
 * NAME, not its id. So renaming an option that has been used would split it in
 * every GROUP BY and nothing would error — the reports would simply be quietly
 * wrong from then on.
 *
 * The page therefore DISABLES the name field on a used option and says why,
 * rather than letting the save fail with a message nobody expected. The server
 * refuses it too; this is the courtesy, that is the guarantee.
 *
 * ⚠ Reads are open to any Oscar user; every save is admin-only, enforced
 * server-side and mirrored here so a non-admin sees the catalogue read-only
 * rather than buttons that 403.
 */
export function HardwareCatalogue({ embedded = false }: { embedded?: boolean } = {}) {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const catalogue = useQuery({
    queryKey: ['hardware-catalogue'],
    queryFn: ({ signal }) => getHardwareCatalogue(signal),
  });

  // ⛔ WHAT WE HOLD, FOR EACH DEVICE (Anthony, 14/10/2026: "the team is confused
  // about the difference of hardware catalogue and stock"). Read from the two APIs
  // that already exist: every stock line says which devices it answers, so no
  // route and no schema is needed to show it from this side.
  //
  // ⚠ NOT FATAL. The catalogue itself carries each device's total, so a stock read
  // that fails costs the expanded detail and nothing else.
  const stockLines = useQuery({
    queryKey: ['stock-items', 'catalogue'],
    queryFn: ({ signal }) => listStockItems({}, signal),
  });
  const stockWarehouses = useQuery({
    // ⚠ The same key the add-device dialog uses, so the list is read once.
    queryKey: ['locations', 'stock'],
    queryFn: ({ signal }) => getLocations({ isStockLocation: true, activeOnly: true }, signal),
  });
  // ⛔ WHAT ANSWERS EACH ACCESSORY (oscar migration 124), in one read of every
  // live (stock line, accessory) pair. ⚠ NOT FATAL: until migration 124 has run
  // this answers 503, which costs the accessory detail and nothing else.
  const accessoryLinks = useQuery({
    queryKey: ['stock-accessory-links'],
    queryFn: ({ signal }) => listAccessoryLinks(signal),
  });

  const [openStock, setOpenStock] = useState<Set<number>>(new Set());
  const toggleStock = (id: number) =>
    setOpenStock((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ⚠ ITS OWN SET. A device and an accessory can share an id, and opening one
  // must not open the other.
  const [openAccessoryStock, setOpenAccessoryStock] = useState<Set<number>>(new Set());
  const toggleAccessoryStock = (id: number) =>
    setOpenAccessoryStock((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const [banner, setBanner] = useState<string | null>(null);
  const [editingDevice, setEditingDevice] = useState<HwCatalogueDevice | null>(null);
  const [editingAccessory, setEditingAccessory] = useState<HwCatalogueAccessory | null>(null);
  const [editingGroup, setEditingGroup] = useState<HwCatalogueGroup | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['hardware-catalogue'] });
    // ⚠ NOTHING TO INVALIDATE FOR THE FORM. Its options query lives in the
    // manage app, in somebody else's browser. The form re-reads on its own
    // stale time, so a change here reaches a requester within minutes rather
    // than instantly — which is the one behaviour the move actually changed.
  };

  const onError = (e: unknown) =>
    setBanner(e instanceof ApiError ? e.message : 'Something went wrong. Please retry.');

  const deviceMut = useMutation({
    /**
     * ⛔ UP TO THREE WRITES BEHIND ONE BUTTON, and only the first is required.
     * The device is the point; the stock line is a convenience. So a failure
     * after the device exists reports what DID happen rather than looking like
     * nothing worked — re-running would otherwise try to create the device
     * again and be refused as a duplicate name.
     */
    mutationFn: async ({ body, stock }: { body: HwDeviceInput; stock?: StockDraft }) => {
      const saved = await saveHardwareDevice(body);
      if (!stock?.hold || stock.locationId === null) return;

      const item = await createStockItem({
        // The one name, used for both. Typed once.
        Name: body.Name,
        ItemType: 'Device',
        Category: null,
        MinQty: null,
        UnitCost: null,
        LocationId: stock.locationId,
        InitialQty: stock.qty,
        IsSerialised: stock.serialised,
      });
      await putStockItemDevices(item.StockItemId, [saved.deviceOptionId]);
    },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['stock-items'] });
      setEditingDevice(null);
      setBanner(null);
    },
    onError,
  });

  const accessoryMut = useMutation({
    mutationFn: saveHardwareAccessory,
    onSuccess: () => { invalidate(); setEditingAccessory(null); setBanner(null); },
    onError,
  });



  const groupMut = useMutation({
    mutationFn: saveHardwareGroup,
    onSuccess: () => { invalidate(); setEditingGroup(null); setBanner(null); },
    onError,
  });

  const devices = catalogue.data?.Devices ?? [];
  const accessories = catalogue.data?.Accessories ?? [];
  const groups = catalogue.data?.Groups ?? [];

  /**
   * The device groups already in use (090), for the editor's picker.
   *
   * ⚠ Includes RETIRED devices' groups on purpose. A group whose only member is
   * retired still exists as far as the board is concerned, and leaving it out
   * would invite somebody to retype it slightly differently.
   */
  const deviceGroupNames = [...new Set(
    devices.map((d) => d.DeviceGroup?.trim()).filter((g): g is string => !!g),
  )].sort((a, b) => a.localeCompare(b));

  // Not memoised: it is a Map over four rows, and `groups` is a fresh array on
  // every render (it comes from `?? []`), so a useMemo keyed on it would rebuild
  // every time anyway while looking as though it did not.
  const groupName = new Map(groups.map((g) => [g.AccessoryGroupId, g.Name]));

  return (
    <section>
      {embedded ? (
        // ⛔ INSIDE THE STOCK PAGE (Anthony, 01/11/2026: "I thought we were combining this into
        // stock"). The page header belongs to Stock; this keeps only what is different about
        // this view.
        <p className="mb-4 text-sm text-darkGrey/80">
          What people can <strong>ask for</strong> on the Hardware and Mobile Device Request form.
          Changes take effect on the form immediately &mdash; there is no deployment. Open a
          device&rsquo;s <em>Details</em> to see the stock lines that answer it, per warehouse, and to link them.
        </p>
      ) : (
        <div className="mb-4">
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
            <Laptop className="h-6 w-6 text-navy" aria-hidden /> Hardware catalogue
          </h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            The devices and accessories the Hardware and Mobile Device Request form offers. Changes
            take effect on the form immediately &mdash; there is no deployment.
          </p>
          <p className="mt-1 text-sm text-darkGrey/80">
            This is what people can <strong>ask for</strong>. What is on the <strong>shelf</strong> is
            on the Stock page &mdash; open a device&rsquo;s <em>Details</em> to see the stock lines
            that answer it, per warehouse, and to link them.
          </p>
        </div>
      )}

      {banner && (
        <div role="alert" className="mb-4 rounded-lg border border-red/40 bg-red/10 p-3 text-sm text-darkGrey">
          {banner}
        </div>
      )}

      {!isAdmin && (
        <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-darkGrey">
          You can see the catalogue but not change it. Editing the devices and accessories the
          request form offers needs the <strong>oscar-admin</strong> role.
        </p>
      )}

      {catalogue.isError && (
        <div role="alert" className="mb-4 rounded-lg border border-red/40 bg-red/10 p-3 text-sm text-darkGrey">
          The catalogue could not be loaded. If migration 075 has not been applied yet, that is why.
        </div>
      )}

      <StandingProjectPanel isAdmin={isAdmin} />

      {/* Who is emailed when an approved request needs a purchase (28/10/2026). */}
      <PurchaseNoticePanel isAdmin={isAdmin} />

      {/* ⛔ THE ONES THAT CAN NEVER BE ALLOCATED. A device-type stock line that
          answers no device is silently ignored by the fulfilment picker (076), so
          nothing else on screen says it is stuck. */}
      {(() => {
        const orphans = orphanLines(stockLines.data ?? []);
        if (orphans.length === 0) return null;
        return (
          <p role="status" className="mb-4 flex items-start gap-2 rounded-lg border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-orange" aria-hidden />
            <span>
              {orphans.length === 1 ? '1 stock line answers' : `${orphans.length} stock lines answer`} no
              device, so a request can never be given {orphans.length === 1 ? 'it' : 'them'} from
              stock: <strong>{orphans.map((o) => o.Name).join(', ')}</strong>. Link{' '}
              {orphans.length === 1 ? 'it' : 'each'} to a device below.
            </span>
          </p>
        );
      })()}

      {/* ── Devices ──────────────────────────────────────────────────────── */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-base font-bold text-navy">Devices</h2>
          {isAdmin && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white"
            onClick={() => setEditingDevice(newDevice(devices))}
          >
            <Plus size={16} aria-hidden /> Add device
          </button>
          )}
        </header>

        {/* Table on desktop, cards on mobile — the tenant's standard. */}
        <div className="hidden overflow-x-auto md:block">
          <table aria-label="Devices" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-lightGrey text-xs uppercase tracking-wide text-slate-600">
                <th className="py-2 pr-3">Device</th>
                {/* ⚠ Beside the name, not buried in Rules. Which group a device
                    belongs to is how the allocation board treats it, and a
                    device nobody has placed needs to be obvious at a glance. */}
                <th className="py-2 pr-3">Group</th>
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Rules</th>
                {/* ⚠ "Indicative" in the heading, not only in the editor. The
                    column sits beside names and sort orders, which are facts;
                    this one is an estimate and has to say so where it is read. */}
                <th className="py-2 pr-3">Indicative cost</th>
                {/* ⚠ What is on the shelf for it, from the Stock page - the column
                    that says the two screens are about the same things. */}
                <th className="py-2 pr-3">Stock</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2"><span className="sr-only">Edit</span></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <Fragment key={d.DeviceOptionId}>
                <tr className="border-b border-lightGrey/60">
                  <td className="py-2 pr-3 font-medium text-darkGrey">{d.Name}</td>
                  <td className="py-2 pr-3"><DeviceGroupCell device={d} /></td>
                  <td className="py-2 pr-3 text-slate-600">{d.SortOrder}</td>
                  <td className="py-2 pr-3"><DeviceRules device={d} /></td>
                  <td className="py-2 pr-3 text-slate-600">{formatMoneyExact(d.UnitCost)}</td>
                  <td className="py-2 pr-3">
                    <StockBadge device={d} />
                    <StockToggle id={d.DeviceOptionId} label={d.Name} open={openStock.has(d.DeviceOptionId)} onToggle={toggleStock} />
                  </td>
                  <td className="py-2 pr-3"><StatusPill active={d.IsActive} used={d.IsUsed} /></td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy hover:bg-navy hover:text-white"
                      aria-label={`Edit ${d.Name}`}
                      onClick={() => setEditingDevice(d)}
                    >
                      <Pencil size={14} aria-hidden /> Edit
                    </button>
                  </td>
                </tr>
                {openStock.has(d.DeviceOptionId) && (
                  <tr className="border-b border-lightGrey/60">
                    <td colSpan={8} className="pb-3">
                      <StockSlot device={d} stock={stockLines} warehouses={stockWarehouses.data ?? []} canEdit={isAdmin} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <ul aria-hidden className="space-y-2 md:hidden">
          {devices.map((d) => (
            <li key={d.DeviceOptionId} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-darkGrey">{d.Name}</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy"
                  aria-label={`Edit ${d.Name}`}
                  onClick={() => setEditingDevice(d)}
                >
                  Edit
                </button>
              </div>
              <div className="mt-1"><DeviceGroupCell device={d} /></div>
              <div className="mt-1"><DeviceRules device={d} /></div>
              <div className="mt-1 text-xs text-slate-600">
                Indicative cost {formatMoneyExact(d.UnitCost)}
              </div>
              <div className="mt-1">
                <StockBadge device={d} />
                <StockToggle id={d.DeviceOptionId} label={d.Name} open={openStock.has(d.DeviceOptionId)} onToggle={toggleStock} />
              </div>
              <div className="mt-1"><StatusPill active={d.IsActive} used={d.IsUsed} /></div>
              {openStock.has(d.DeviceOptionId) && (
                <div className="mt-2">
                  <StockSlot device={d} stock={stockLines} warehouses={stockWarehouses.data ?? []} canEdit={isAdmin} />
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Accessories ──────────────────────────────────────────────────── */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-base font-bold text-navy">Components and accessories</h2>
          {isAdmin && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white"
            onClick={() => setEditingAccessory(newAccessory(accessories))}
          >
            <Plus size={16} aria-hidden /> Add component / accessory
          </button>
          )}
        </header>

        <div className="hidden overflow-x-auto md:block">
          <table aria-label="Components and accessories" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-lightGrey text-xs uppercase tracking-wide text-slate-600">
                <th className="py-2 pr-3">Component / accessory</th>
                <th className="py-2 pr-3">Offered for</th>
                <th className="py-2 pr-3">Only one of</th>
                <th className="py-2 pr-3">Indicative cost</th>
                {/* ⚠ What is on the shelf for it (oscar migration 124) - the column
                    a device already had. */}
                <th className="py-2 pr-3">Stock</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2"><span className="sr-only">Edit</span></th>
              </tr>
            </thead>
            <tbody>
              {accessories.map((a) => (
                <Fragment key={a.AccessoryOptionId}>
                <tr className="border-b border-lightGrey/60">
                  <td className="py-2 pr-3 font-medium text-darkGrey">{a.Name}</td>
                  <td className="py-2 pr-3 text-slate-600">
                    {a.ForDevices.length > 0
                      ? forDevicesText(a)
                      : (
                        <span className="inline-flex items-center gap-1 text-red">
                          <AlertTriangle size={14} aria-hidden /> No device — can never be offered
                        </span>
                      )}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {a.AccessoryGroupId ? groupName.get(a.AccessoryGroupId) ?? '—' : '—'}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{formatMoneyExact(a.UnitCost)}</td>
                  <td className="py-2 pr-3">
                    <AccessoryBadgeSlot accessory={a} stock={stockLines.data} links={accessoryLinks.data} />
                    <StockToggle
                      id={a.AccessoryOptionId}
                      label={`accessory ${a.Name}`}
                      open={openAccessoryStock.has(a.AccessoryOptionId)}
                      onToggle={toggleAccessoryStock}
                    />
                  </td>
                  <td className="py-2 pr-3"><StatusPill active={a.IsActive} used={a.IsUsed} /></td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy hover:bg-navy hover:text-white"
                      aria-label={`Edit ${a.Name}`}
                      onClick={() => setEditingAccessory(a)}
                    >
                      <Pencil size={14} aria-hidden /> Edit
                    </button>
                  </td>
                </tr>
                {openAccessoryStock.has(a.AccessoryOptionId) && (
                  <tr className="border-b border-lightGrey/60">
                    <td colSpan={7} className="pb-3">
                      <AccessorySlot
                        accessory={a}
                        accessories={accessories}
                        stock={stockLines}
                        links={accessoryLinks}
                        warehouses={stockWarehouses.data ?? []}
                        canEdit={isAdmin}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <ul aria-hidden className="space-y-2 md:hidden">
          {accessories.map((a) => (
            <li key={a.AccessoryOptionId} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-darkGrey">{a.Name}</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy"
                  aria-label={`Edit ${a.Name}`}
                  onClick={() => setEditingAccessory(a)}
                >
                  Edit
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {a.ForDevices.length > 0 ? forDevicesText(a) : 'No device — can never be offered'}
              </p>
              <div className="mt-1">
                <AccessoryBadgeSlot accessory={a} stock={stockLines.data} links={accessoryLinks.data} />
                <StockToggle
                  id={a.AccessoryOptionId}
                  label={`accessory ${a.Name}`}
                  open={openAccessoryStock.has(a.AccessoryOptionId)}
                  onToggle={toggleAccessoryStock}
                />
              </div>
              <div className="mt-1"><StatusPill active={a.IsActive} used={a.IsUsed} /></div>
              {openAccessoryStock.has(a.AccessoryOptionId) && (
                <div className="mt-2">
                  <AccessorySlot
                    accessory={a}
                    accessories={accessories}
                    stock={stockLines}
                    links={accessoryLinks}
                    warehouses={stockWarehouses.data ?? []}
                    canEdit={isAdmin}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {editingDevice && (
        <DeviceEditor
          device={editingDevice}
          /* ⚠ The groups that already exist, so the picker offers them rather
             than letting somebody type "Laptops" beside "Laptop". */
          groupNames={deviceGroupNames}
          saving={deviceMut.isPending}
          onCancel={() => setEditingDevice(null)}
          onSave={(body, stock) => deviceMut.mutate({ body, stock })}
        />
      )}

      <GroupsSection
        groups={groups}
        accessories={accessories}
        onAdd={() => setEditingGroup({ AccessoryGroupId: 0, Name: '' })}
        onEdit={setEditingGroup}
      />

      {editingGroup && (
        <GroupEditor
          group={editingGroup}
          inUse={accessories.filter(
            (a) => a.AccessoryGroupId === editingGroup.AccessoryGroupId).length}
          saving={groupMut.isPending}
          onCancel={() => setEditingGroup(null)}
          onSave={(body) => groupMut.mutate(body)}
        />
      )}

      {editingAccessory && (
        <AccessoryEditor
          accessory={editingAccessory}
          devices={devices}
          groups={groups}
          saving={accessoryMut.isPending}
          onCancel={() => setEditingAccessory(null)}
          onSave={(body) => accessoryMut.mutate(body)}
        />
      )}
    </section>
  );
}

/** A new row, sorted after everything already there. */
function newDevice(existing: HwCatalogueDevice[]): HwCatalogueDevice {
  return {
    DeviceOptionId: 0,
    Name: '',
    SortOrder: nextSortOrder(existing.map((d) => d.SortOrder)),
    IsAccessoryOnly: false,
    IsBranchUse: false,
    // ⚠ Not watched until somebody says a number. Nought would mean "tell me
    // when we have none", which is a real instruction nobody has given.
    MinQty: null,
    // ⚠ No price until somebody types one. Nought would prefill a free
    // laptop on the purchase screen, which reads as deliberate (083).
    UnitCost: null,
    // ⚠ Ungrouped until somebody says otherwise, which behaves exactly as every
    // device did before 090. Nothing is forced into a group.
    DeviceGroup: null,
    AssetCategory: null,
    IsGroupDefault: false,
    TotalOnHand: 0,
    StockLines: 0,
    IsLow: false,
    // Set by migration 057 and not editable here, so a new device never has it.
    RequiresFleetNumber: false,
    AsksAccessoryCondition: false,
    CanBeLocked: false,
    HasMobileNumber: false,
    IsActive: true,
    IsUsed: false,
  };
}

/**
 * The devices a component is offered for, with "(required)" on the ones it is required for.
 *
 * ⚠ ForDevices and DeviceOptionIds come from the same query in the same order, so they are parallel.
 */
function forDevicesText(a: HwCatalogueAccessory): string {
  return a.ForDevices
    .map((name, i) => (a.RequiredDeviceOptionIds.includes(a.DeviceOptionIds[i]) ? `${name} (required)` : name))
    .join(', ');
}

function newAccessory(existing: HwCatalogueAccessory[]): HwCatalogueAccessory {
  return {
    AccessoryOptionId: 0,
    Name: '',
    SortOrder: nextSortOrder(existing.map((a) => a.SortOrder)),
    AccessoryGroupId: null,
    IsActive: true,
    DeviceOptionIds: [],
    ForDevices: [],
    RequiredDeviceOptionIds: [],
    IsUsed: false,
    // ⚠ No price until somebody types one. Nought would price a docking
    // station at nothing, which reads as deliberate (086).
    UnitCost: null,
  };
}

/** Tens, so a later insertion between two rows needs no renumbering. */
function nextSortOrder(existing: number[]): number {
  return existing.length === 0 ? 10 : Math.max(...existing) + 10;
}

function DeviceRules({ device }: { device: HwCatalogueDevice }) {
  const rules: string[] = [];
  if (device.IsAccessoryOnly) rules.push('The “no device needed” option');
  if (device.AsksAccessoryCondition) rules.push('Asks accessories question');
  if (device.CanBeLocked) rules.push('Can be locked');
  if (device.HasMobileNumber) rules.push('Has a mobile number');
  if (device.IsBranchUse) rules.push('Location only');
  if (device.IsGroupDefault) rules.push('Group default');

  return (
    <span className="text-xs text-slate-600">
      {rules.length > 0 ? rules.join(' · ') : 'None'}
    </span>
  );
}

function StatusPill({ active, used }: { active: boolean; used: boolean }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span
        className={`rounded px-2 py-0.5 text-xs font-semibold ${
          active ? 'bg-green/15 text-green' : 'bg-slate-200 text-slate-600'
        }`}
      >
        {active ? 'Offered' : 'Retired'}
      </span>
      {used && (
        <span className="rounded bg-lightBlue/60 px-2 py-0.5 text-xs font-medium text-navy">
          Name locked
        </span>
      )}
    </span>
  );
}

// ── Editors ────────────────────────────────────────────────────────────────

function Modal({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-4 sm:p-5"
      >
        <h3 className="mb-3 font-heading text-base font-bold text-navy">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/**
 * ⛔ The name field is DISABLED on a used option, with the reason stated. The
 * server refuses the rename too — this is so an administrator learns why
 * BEFORE typing a new name rather than after.
 *
 * ⚠ THE BOX GOES DIRECTLY UNDER THE LABEL. It used to sit below a four-line
 * paragraph about renaming, which pushed the only field you have to fill in
 * halfway down the dialog and made it genuinely hard to find — reported
 * 01/10/2026. Explanations belong after the control they explain.
 *
 * ⚠ And the renaming note is NOT shown on a new one. Nothing has been raised
 * against a device that does not exist yet, so on Add it was advice about a
 * situation that cannot arise, printed above the first thing you type.
 */
function NameField({
  value, used, isNew = false, what, onChange,
}: { value: string; used: boolean; isNew?: boolean; what: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
        Name
      </span>
      <input
        className="input"
        value={value}
        disabled={used}
        autoFocus={isNew}
        onChange={(e) => onChange(e.target.value)}
      />
      {used && (
        <p className="mt-1 text-xs text-slate-600">
          Some older requests record this {what} by name only, so renaming it would cut them
          adrift from everything after. Retire it and add a new one instead.
        </p>
      )}
      {!used && !isNew && (
        <p className="mt-1 text-xs text-slate-600">
          Renaming is allowed, but it does not rewrite history: requests already raised keep the
          name they were given. Reports group by the option itself, so they stay together.
        </p>
      )}
    </label>
  );
}

/**
 * Which group a device is in, and what it stands in for (090).
 *
 * ⚠ "Not grouped" is SAID, not left blank. An empty cell reads as a rendering
 * gap; the words are what make somebody go and place it — and an ungrouped
 * device is exactly the state that produced the "no laptop for 52 roles" hole.
 */
/**
 * Show or hide what we hold for one device.
 *
 * ⚠ A button with a name that says WHICH device, not a bare "Details" - the table
 * has a row of them and a screen reader hears only the label.
 */
function StockToggle({
  id,
  label,
  open,
  onToggle,
}: {
  id: number;
  /** What to call it to a screen reader: "Laptop", or "accessory Laptop bag". */
  label: string;
  open: boolean;
  onToggle: (id: number) => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={`${open ? 'Hide' : 'Show'} stock for ${label}`}
      className="ml-2 text-xs font-semibold text-navy hover:underline"
      onClick={() => onToggle(id)}
    >
      {open ? 'Hide' : 'Details'}
    </button>
  );
}

/**
 * The expanded panel, or why it is not there yet.
 *
 * ⚠ NOT FATAL. The badge beside it comes from the catalogue, which already
 * carries each device's totals; only this detail needs the stock list, so a
 * failure to read it says so here and costs nothing else.
 */
function StockSlot({
  device,
  stock,
  warehouses,
  canEdit,
}: {
  device: HwCatalogueDevice;
  stock: { data?: StockItem[]; isError: boolean };
  warehouses: { LocationId: number; LocationDesc: string }[];
  canEdit: boolean;
}) {
  if (stock.isError) {
    return (
      <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
        Could not load the stock lines. The total beside the device is still right.
      </p>
    );
  }
  if (!stock.data) return <p className="text-sm text-slate-600">Loading what we hold…</p>;

  return <DeviceStockPanel device={device} items={stock.data} warehouses={warehouses} canEdit={canEdit} />;
}

/**
 * The accessory's expanded panel, or why it is not there yet (oscar migration 124).
 *
 * ⚠ NOT FATAL, and it needs BOTH reads: the stock lines, and the links that say
 * which of them answer which accessory. Either failing says so here and costs
 * nothing else - the badge beside it is made from the same two, so it is held back
 * with the panel rather than showing a total it cannot back up.
 */
function AccessorySlot({
  accessory,
  accessories,
  stock,
  links,
  warehouses,
  canEdit,
}: {
  accessory: HwCatalogueAccessory;
  accessories: HwCatalogueAccessory[];
  stock: { data?: StockItem[]; isError: boolean };
  links: { data?: AccessoryLink[]; isError: boolean };
  warehouses: { LocationId: number; LocationDesc: string }[];
  canEdit: boolean;
}) {
  if (stock.isError || links.isError) {
    return (
      <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
        Could not load what answers this accessory. If this keeps happening, contact Onsite IT Support.
      </p>
    );
  }
  if (!stock.data || !links.data) return <p className="text-sm text-slate-600">Loading what we hold…</p>;

  return (
    <AccessoryStockPanel
      accessory={accessory}
      accessories={accessories}
      items={stock.data}
      links={links.data}
      warehouses={warehouses}
      canEdit={canEdit}
    />
  );
}

/**
 * The accessory's badge, once there is something to say.
 *
 * ⚠ BLANK, NOT "NOT STOCKED", WHILE THE LINKS ARE UNKNOWN. Drawing "Not stocked"
 * before the pairs have arrived - or when they cannot be read - would tell an
 * administrator something is unstocked that may simply not have loaded yet.
 */
function AccessoryBadgeSlot({
  accessory,
  stock,
  links,
}: {
  accessory: HwCatalogueAccessory;
  stock: StockItem[] | undefined;
  links: AccessoryLink[] | undefined;
}) {
  if (!stock || !links) return <span className="text-xs text-slate-500">—</span>;

  return <AccessoryStockBadge lines={accessoryLinesFor(accessory.AccessoryOptionId, stock, links)} />;
}

function DeviceGroupCell({ device }: { device: HwCatalogueDevice }) {
  if (!device.DeviceGroup) {
    return <span className="text-xs text-slate-500">Not grouped</span>;
  }

  return (
    <span className="text-xs text-darkGrey">
      <span className="font-semibold">{device.DeviceGroup}</span>
      {device.AssetCategory && (
        <span className="text-slate-500"> · replaces {device.AssetCategory}</span>
      )}
    </span>
  );
}

function DeviceEditor({
  device, groupNames, saving, onCancel, onSave,
}: {
  device: HwCatalogueDevice;
  groupNames: string[];
  saving: boolean;
  onCancel: () => void;
  onSave: (body: {
    DeviceOptionId?: number | null; Name: string; SortOrder: number;
    IsAccessoryOnly: boolean; AsksAccessoryCondition: boolean;
    CanBeLocked: boolean; HasMobileNumber: boolean; IsBranchUse: boolean;
    IsActive: boolean; MinQty: number | null; UnitCost: number | null;
    DeviceGroup: string | null; AssetCategory: string | null; IsGroupDefault: boolean;
  }, stock?: StockDraft) => void;
}) {
  const [draft, setDraft] = useState(device);
  const isNew = device.DeviceOptionId === 0;

  const [stock, setStock] = useState<StockDraft>({
    hold: false, locationId: null, qty: 0, serialised: false,
  });

  // ⚠ Stock locations only. IsStockLocation is what makes a place able to hold
  // a count; a branch is not one.
  const warehouses = useQuery({
    queryKey: ['locations', 'stock'],
    queryFn: ({ signal }) => getLocations({ isStockLocation: true, activeOnly: true }, signal),
    enabled: isNew && !draft.IsAccessoryOnly,
  });

  // ⛔ "Hold stock" with no warehouse would create a count nobody can go and
  // fetch, which the stock API refuses anyway — said here so it is refused
  // before the device has already been created.
  const stockIncomplete = stock.hold && stock.locationId === null;

  return (
    <Modal title={isNew ? 'Add device' : `Edit ${device.Name}`}>
      <div className="space-y-3">
        <NameField
          value={draft.Name}
          used={draft.IsUsed}
          isNew={isNew}
          what="device"
          onChange={(Name) => setDraft({ ...draft, Name })}
        />

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Order
          </span>
          <input
            type="number"
            className="input"
            value={draft.SortOrder}
            onChange={(e) => setDraft({ ...draft, SortOrder: Number(e.target.value) })}
          />
        </label>

        <fieldset className="rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Rules
          </legend>
          <p className="mb-2 text-xs text-slate-600">
            These decide which questions the form asks when this device is chosen.
          </p>

          {/* ⛔ NOT "this item is an accessory", which is how it read and how it
              was ticked on a webcam (06/10/2026). It marks the SINGLE "no
              device needed" escape hatch, and manage picks that with
              FirstOrDefault — so a second one silently becomes the option for
              everybody. The label now says what it is. */}
          <Toggle
            label="This is the &ldquo;no device needed&rdquo; option"
            hint="Marks the one entry that means &ldquo;I don&rsquo;t need a device, just accessories&rdquo;. There is only one, and it cannot be combined with a real device. This is NOT how you mark an item as an accessory — leave it off for a webcam or a dock."
            checked={draft.IsAccessoryOnly}
            onChange={(IsAccessoryOnly) => setDraft({ ...draft, IsAccessoryOnly })}
          />
          <Toggle
            label="Asks the accessories question"
            hint="On a replacement, ask whether the current accessories are present and working. Devices without this always show the accessory list."
            checked={draft.AsksAccessoryCondition}
            onChange={(AsksAccessoryCondition) => setDraft({ ...draft, AsksAccessoryCondition })}
          />
          <Toggle
            icon={<Lock size={14} aria-hidden />}
            label="Can be locked"
            hint="Show the device-lock advisory when this device is reported lost or stolen."
            checked={draft.CanBeLocked}
            onChange={(CanBeLocked) => setDraft({ ...draft, CanBeLocked })}
          />
          <Toggle
            icon={<Smartphone size={14} aria-hidden />}
            label="Has a mobile number"
            hint="Ask where to divert calls when it is lost or stolen. Narrower than “can be locked” — a tablet has no number to divert."
            checked={draft.HasMobileNumber}
            onChange={(HasMobileNumber) => setDraft({ ...draft, HasMobileNumber })}
          />
          <Toggle
            icon={<Building2 size={14} aria-hidden />}
            label="Only for a location, never a person"
            hint="Kit for a site rather than a person — a workshop monitor. Ticking this on the FIRST device is what adds “a location” to the request form. ⛔ It also REMOVES this item from personal requests: from 06/10/2026 branch kit is offered to locations only, and a department can no longer be allocated to it."
            checked={draft.IsBranchUse}
            onChange={(IsBranchUse) => setDraft({ ...draft, IsBranchUse })}
          />

        </fieldset>

        {/* ⛔ THE GROUP (090). Four laptop variants were allocated SEPARATELY,
            so every one of them was restricted and a role on none of the four
            lists was offered no laptop at all. The group is what lets the
            allocation board treat them as one choice. */}
        <fieldset className="rounded border border-slate-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Group
          </legend>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Belongs with
            </span>
            {/* ⚠ A datalist, not a select: the existing groups are offered so
                nobody invents "Laptops" beside "Laptop", but a genuinely new
                group can still be typed without a separate screen to create it
                on first. */}
            <input
              className="input"
              list="device-groups"
              maxLength={40}
              placeholder="Laptop, Monitor, Headset…"
              value={draft.DeviceGroup ?? ''}
              onChange={(e) => setDraft({
                ...draft,
                DeviceGroup: e.target.value,
                // ⛔ Clearing the group clears the default with it. A default
                // outside a group is refused by the server and by
                // CK_HwDeviceOption_GroupDefault, and leaving the tick on
                // screen would offer a save that cannot succeed.
                IsGroupDefault: e.target.value.trim() ? draft.IsGroupDefault : false,
              })}
            />
            <datalist id="device-groups">
              {groupNames.map((g) => <option key={g} value={g} />)}
            </datalist>
            <span className="mt-1 block text-xs text-slate-500">
              What makes the variations of one thing siblings — Standard, High Spec, OLT and
              Tough Book are all <strong>Laptop</strong>. A role gets one from each group.
              Leave it empty and nothing changes for this device.
            </span>
          </label>

          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Replaces
            </span>
            <select
              className="input"
              value={draft.AssetCategory ?? ''}
              onChange={(e) => setDraft({ ...draft, AssetCategory: e.target.value || null })}
            >
              <option value="">— nothing —</option>
              {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Which kind of asset in Oscar this stands in for. On a replacement request the
              device list is narrowed to what matches the device being replaced.
              ⚠ Monitors, headsets and webcams are all <strong>Other</strong> — the asset
              register has no finer word for a peripheral.
            </span>
          </label>

          <div className="mt-3">
            <Toggle
              icon={<Star size={14} aria-hidden />}
              label="The one a role gets by default"
              hint="When nobody has allocated this group to somebody's role, this is the variation they are offered. ⛔ Without one, a role with no entitlement in the group is offered NOTHING from it. Only one device per group can hold this."
              checked={draft.IsGroupDefault}
              onChange={(IsGroupDefault) => setDraft({ ...draft, IsGroupDefault })}
            />
            {!draft.DeviceGroup?.trim() && (
              <p className="mt-1 text-xs text-slate-500">
                Put the device in a group first — a default only means something inside one.
              </p>
            )}
          </div>
        </fieldset>

        {/* ⛔ THE THRESHOLD LIVES HERE, NOT ON THE STOCK LINE (080). A stock
            line is one warehouse, so a threshold on it would mean "twenty at
            Sydney AND twenty at Brisbane". The device is the only thing that
            groups the warehouses together. */}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Hold at least
          </span>
          <input
            type="number"
            min={0}
            className="input"
            value={draft.MinQty ?? ''}
            onChange={(e) => setDraft({
              ...draft,
              MinQty: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
            })}
          />
          <p className="mt-1 text-xs text-slate-600">
            Across <strong>every</strong> warehouse. Stock drops below this and it reads
            <em> Low</em> on the Stock page. Leave empty to not watch it at all — nought is a
            different instruction, it means tell me when we have none.
          </p>
          {draft.MinQty !== null && (draft.StockLines ?? 0) === 0 && (
            <p className="mt-1 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
              ⚠ No stock line answers this device yet, so it will read Low the moment you save.
            </p>
          )}
        </label>

        {/* ⛔ THE PRICE IS A DEFAULT, NOT A DECISION (083). It prefills the
            amount when a request is fulfilled by purchase; the fulfiller sees
            it and can change it before anything is posted. Said on the screen
            so nobody treats this box as the authority on what was paid. */}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Indicative cost (ex GST)
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input"
            value={draft.UnitCost ?? ''}
            onChange={(e) => setDraft({
              ...draft,
              UnitCost: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
            })}
          />
          <p className="mt-1 text-xs text-slate-600">
            What one of these is reckoned to cost. It fills in the amount when a request for this
            device is fulfilled by <em>Purchase Required</em>, and whoever allocates it can change
            the figure before it reaches the Project Tracker — so this is a starting point, not a
            record of what was paid. Leave empty and the box starts blank.
          </p>
        </label>

        <Toggle
          label="Offered on the form"
          hint="Turn off to retire it. Existing requests keep it; nobody can choose it again."
          checked={draft.IsActive}
          onChange={(IsActive) => setDraft({ ...draft, IsActive })}
        />

        {/* ⛔ TYPE THE NAME ONCE. The stock line and the catalogue device are
            two different records on purpose — one is what the warehouse counts,
            the other is what a requester reads, and they are many-to-many
            across warehouses. But when they do line up, there is no reason to
            type the name twice on two screens.

            ⚠ Only on a NEW device, and never for the accessory-only option:
            "I don't need a device" is not something you can hold on a shelf. */}
        {/* ⚠ SAID, NOT JUST HIDDEN. The block disappearing is what got
            reported — nothing on screen explained why, so it read as a bug. */}
        {isNew && draft.IsAccessoryOnly && (
          <p className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
            The &ldquo;no device needed&rdquo; option is not a thing on a shelf, so it cannot hold
            stock. If you are adding a real item like a webcam, turn that box off and the stock
            question comes back.
          </p>
        )}

        {isNew && !draft.IsAccessoryOnly && (
          <fieldset className="rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
              Stock
            </legend>

            <Toggle
              label="Hold stock for it at a warehouse"
              hint="Creates a stock line with this same name, already set to answer this device. Leave off for anything bought per request — an iPhone from the telco, a label printer."
              checked={stock.hold}
              onChange={(hold) => setStock({ ...stock, hold })}
            />

            {stock.hold && (
              <div className="mt-2 space-y-2 border-t border-slate-200 pt-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Warehouse
                  </span>
                  <select
                    className="input"
                    value={stock.locationId ?? ''}
                    onChange={(e) => setStock({
                      ...stock,
                      locationId: e.target.value ? Number(e.target.value) : null,
                    })}
                  >
                    <option value="">Select…</option>
                    {(warehouses.data ?? []).map((l) => (
                      <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>
                    ))}
                  </select>
                  {warehouses.isError && (
                    <p className="mt-1 text-xs text-red">
                      Could not load the warehouse list. Add the device, then add the stock line
                      on the Stock page.
                    </p>
                  )}
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Opening quantity
                  </span>
                  <input
                    type="number"
                    className="input"
                    value={stock.qty}
                    onChange={(e) => setStock({ ...stock, qty: Number(e.target.value) || 0 })}
                  />
                </label>

                <Toggle
                  label="Tracked by serial"
                  hint="Each unit is an individual machine. A request allocates these one at a time, and issuing one asks which machine went out."
                  checked={stock.serialised}
                  onChange={(serialised) => setStock({ ...stock, serialised })}
                />

                <p className="text-xs text-slate-600">
                  ⚠ One warehouse. The same device at a second warehouse is a second stock line,
                  added from the Stock page.
                </p>
              </div>
            )}
          </fieldset>
        )}

        <EditorButtons
          saving={saving}
          disabled={draft.Name.trim().length === 0 || stockIncomplete}
          onCancel={onCancel}
          onSave={() => onSave({
            DeviceOptionId: isNew ? null : draft.DeviceOptionId,
            Name: draft.Name.trim(),
            SortOrder: draft.SortOrder,
            IsAccessoryOnly: draft.IsAccessoryOnly,
            AsksAccessoryCondition: draft.AsksAccessoryCondition,
            CanBeLocked: draft.CanBeLocked,
            HasMobileNumber: draft.HasMobileNumber,
            IsBranchUse: draft.IsBranchUse,
            IsActive: draft.IsActive,
            MinQty: draft.MinQty,
            UnitCost: draft.UnitCost,
            // ⚠ Trimmed to null, so a cleared box and an untouched one mean the
            // same thing rather than creating a group called "".
            DeviceGroup: draft.DeviceGroup?.trim() || null,
            AssetCategory: draft.AssetCategory || null,
            // ⛔ A default only means something inside a group. Sent as false
            // when there is no group, so a cleared group cannot leave a
            // dangling default the server has to refuse.
            IsGroupDefault: draft.DeviceGroup?.trim() ? draft.IsGroupDefault : false,
          }, stock)}
        />
      </div>
    </Modal>
  );
}

function AccessoryEditor({
  accessory, devices, groups, saving, onCancel, onSave,
}: {
  accessory: HwCatalogueAccessory;
  devices: HwCatalogueDevice[];
  groups: { AccessoryGroupId: number; Name: string }[];
  saving: boolean;
  onCancel: () => void;
  onSave: (body: {
    AccessoryOptionId?: number | null; Name: string; SortOrder: number;
    AccessoryGroupId: number | null; IsActive: boolean; DeviceOptionIds: number[];
    RequiredDeviceOptionIds: number[]; UnitCost: number | null;
  }) => void;
}) {
  const [draft, setDraft] = useState(accessory);
  const isNew = accessory.AccessoryOptionId === 0;

  // ⛔ IDS, NOT NAMES. The manage original resolved the mapping back from
  // device NAMES, which silently dropped any accessory whose device had been
  // renamed. The API returns both now: names for the list, ids for this.
  const [chosen, setChosen] = useState<number[]>(() => [...accessory.DeviceOptionIds]);

  // ⛔ REQUIRED, PER DEVICE (Marty, 29/10/2026: a power adapter is a required component, not an
  // optional accessory). Only meaningful for a device it is offered for, so unticking the device
  // drops the flag with it - the server refuses one that is not also offered.
  const [required, setRequired] = useState<number[]>(() => [...accessory.RequiredDeviceOptionIds]);
  const requiredHere = required.filter((id) => chosen.includes(id));

  const noDevice = chosen.length === 0 && draft.IsActive;

  // ⛔ "Only one of" means at most one; "required" means they get it regardless. Said here, because the
  // server refuses the pair and the person should not have to find out by saving.
  const requiredInGroup = requiredHere.length > 0 && draft.AccessoryGroupId !== null;

  return (
    <Modal title={isNew ? 'Add component / accessory' : `Edit ${accessory.Name}`}>
      <div className="space-y-3">
        <NameField
          value={draft.Name}
          used={draft.IsUsed}
          isNew={isNew}
          what="accessory"
          onChange={(Name) => setDraft({ ...draft, Name })}
        />

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Order
          </span>
          <input
            type="number"
            className="input"
            value={draft.SortOrder}
            onChange={(e) => setDraft({ ...draft, SortOrder: Number(e.target.value) })}
          />
        </label>

        <fieldset className="rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Offered for
          </legend>
          <p className="mb-2 text-xs text-slate-600">
            It appears only when one of these devices is chosen. Tick <strong>Required</strong> for a
            device that always needs it &mdash; the power adapter for a Starlink, say. Leave it unticked
            for an optional extra.
          </p>
          <div className="space-y-1">
            {devices.map((d) => (
              <div key={d.DeviceOptionId} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-navy"
                    checked={chosen.includes(d.DeviceOptionId)}
                    onChange={(e) => setChosen(e.target.checked
                      ? [...chosen, d.DeviceOptionId]
                      : chosen.filter((id) => id !== d.DeviceOptionId))}
                  />
                  {d.Name}
                  {!d.IsActive && <span className="text-xs text-slate-500">(retired)</span>}
                </label>
                {chosen.includes(d.DeviceOptionId) && (
                  <label className="flex items-center gap-1 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      className="accent-navy"
                      aria-label={`Required for ${d.Name}`}
                      checked={required.includes(d.DeviceOptionId)}
                      onChange={(e) => setRequired(e.target.checked
                        ? [...required, d.DeviceOptionId]
                        : required.filter((id) => id !== d.DeviceOptionId))}
                    />
                    Required
                  </label>
                )}
              </div>
            ))}
          </div>
          {noDevice && (
            <p className="mt-2 text-xs text-red">
              Choose at least one device, or this accessory can never be offered.
            </p>
          )}
          {requiredInGroup && (
            <p role="alert" className="mt-2 text-xs text-red">
              A required component cannot be in an &lsquo;only one of&rsquo; group. Take it out of the
              group below, or make it optional.
            </p>
          )}
        </fieldset>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Only one of
          </span>
          <p className="mb-1 text-xs text-slate-600">
            Put accessories in the same group when a requester may choose at most one of them —
            two chargers, say. Choosing none is always allowed.
          </p>
          <select
            className="input"
            value={draft.AccessoryGroupId ?? ''}
            onChange={(e) => setDraft({
              ...draft,
              AccessoryGroupId: e.target.value === '' ? null : Number(e.target.value),
            })}
          >
            <option value="">No group</option>
            {groups.map((g) => (
              <option key={g.AccessoryGroupId} value={g.AccessoryGroupId}>{g.Name}</option>
            ))}
          </select>
        </label>

        {/* ⛔ THE SAME NUMBER AS THE DEVICE'S (086), and the same caveat: it is
            a budget figure, not a record of what was paid. ⚠ Unlike the
            device's it does not prefill anything yet — said here so nobody
            prices twenty accessories expecting the purchase box to add up. */}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Indicative cost (ex GST)
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input"
            value={draft.UnitCost ?? ''}
            onChange={(e) => setDraft({
              ...draft,
              UnitCost: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
            })}
          />
          <p className="mt-1 text-xs text-slate-600">
            What one of these is reckoned to cost. Shown on the catalogue for budgeting; it does
            not yet fill in the amount on a purchase, because a request can ask for several
            accessories and nothing records how many of each. Leave empty and it reads as a dash.
          </p>
        </label>

        <Toggle
          label="Offered on the form"
          hint="Turn off to retire it. Existing requests keep it; nobody can choose it again."
          checked={draft.IsActive}
          onChange={(IsActive) => setDraft({ ...draft, IsActive })}
        />

        <EditorButtons
          saving={saving}
          disabled={draft.Name.trim().length === 0 || noDevice || requiredInGroup}
          onCancel={onCancel}
          onSave={() => onSave({
            AccessoryOptionId: isNew ? null : draft.AccessoryOptionId,
            Name: draft.Name.trim(),
            SortOrder: draft.SortOrder,
            AccessoryGroupId: draft.AccessoryGroupId,
            IsActive: draft.IsActive,
            DeviceOptionIds: chosen,
            // ⚠ Only devices it is still offered for: a flag left on an unticked device would be refused.
            RequiredDeviceOptionIds: requiredHere,
            UnitCost: draft.UnitCost,
          })}
        />
      </div>
    </Modal>
  );
}

function Toggle({
  label, hint, checked, onChange, icon,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  icon?: React.ReactNode;
}) {
  return (
    <label className="mt-2 flex cursor-pointer items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1 accent-navy"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="inline-flex items-center gap-1 font-medium text-darkGrey">
          {icon}{label}
        </span>
        {hint && <span className="block text-xs text-slate-600">{hint}</span>}
      </span>
    </label>
  );
}

function EditorButtons({
  saving, disabled, onCancel, onSave,
}: { saving: boolean; disabled: boolean; onCancel: () => void; onSave: () => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-2 pt-2">
      <button
        type="button"
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-darkGrey"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        type="button"
        className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        disabled={saving || disabled}
        onClick={onSave}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}


/**
 * Accessory groups — the "only one of" sets.
 *
 * ⛔ WHY THIS SECTION EXISTS. The groups drove a dropdown on the accessory
 * editor and could be CREATED but never renamed or retired: the endpoint was
 * INSERT-only. So a typo was permanent and a group that stopped being needed
 * stayed in the list for ever. It looked static because it was.
 */
function GroupsSection({
  groups, accessories, onAdd, onEdit,
}: {
  groups: HwCatalogueGroup[];
  accessories: HwCatalogueAccessory[];
  onAdd: () => void;
  onEdit: (g: HwCatalogueGroup) => void;
}) {
  const countFor = (id: number) =>
    accessories.filter((a) => a.AccessoryGroupId === id).length;

  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base font-bold text-navy">Accessory groups</h2>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white hover:bg-navy/90"
        >
          <Plus size={16} aria-hidden /> Add group
        </button>
      </div>

      <p className="mt-1 text-xs text-slate-600">
        A requester may choose at most one accessory from each group — two chargers, say.
        Choosing none is always allowed.
      </p>

      {groups.length === 0 && (
        <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          No groups. Every accessory can be chosen independently.
        </p>
      )}

      {groups.length > 0 && (
        <table aria-label="Accessory groups" className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
              <th className="py-2 pr-3">Group</th>
              <th className="py-2 pr-3">Accessories in it</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const used = countFor(g.AccessoryGroupId);
              return (
                <tr key={g.AccessoryGroupId} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-medium text-darkGrey">{g.Name}</td>
                  <td className="py-2 pr-3">
                    {used === 0
                      ? <span className="text-xs text-slate-500">None — this group does nothing</span>
                      : <span className="text-xs text-slate-600">
                          {accessories
                            .filter((a) => a.AccessoryGroupId === g.AccessoryGroupId)
                            .map((a) => a.Name)
                            .join(', ')}
                        </span>}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      aria-label={`Edit ${g.Name}`}
                      onClick={() => onEdit(g)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline"
                    >
                      <Pencil size={14} aria-hidden /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function GroupEditor({
  group, inUse, saving, onCancel, onSave,
}: {
  group: HwCatalogueGroup;
  inUse: number;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: HwGroupInput) => void;
}) {
  const [name, setName] = useState(group.Name);
  const isNew = group.AccessoryGroupId === 0;

  return (
    <Modal title={isNew ? 'Add group' : `Edit ${group.Name}`}>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
          Name
        </span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        {/* The asymmetry with devices and accessories is worth stating: those
            cannot be renamed once used, because a submission stores their NAME.
            A group name is never stored on a submission, so it is always safe. */}
        <span className="mt-1 block text-xs text-slate-500">
          Safe to rename at any time — a group name is never recorded on a request.
        </span>
      </label>

      {!isNew && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          {inUse > 0 ? (
            <p className="text-xs text-slate-600">
              {inUse === 1 ? '1 accessory is' : `${inUse} accessories are`} in this group, so it
              cannot be retired yet. Move {inUse === 1 ? 'it' : 'them'} to another group, or to no
              group, first — otherwise they would quietly become independently selectable.
            </p>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={() => onSave({
                AccessoryGroupId: group.AccessoryGroupId,
                Name: name.trim(),
                IsDeleted: true,
              })}
              className="inline-flex items-center gap-1 text-xs font-semibold text-red hover:underline"
            >
              <Trash2 size={14} aria-hidden /> Retire this group
            </button>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-darkGrey">
          Cancel
        </button>
        <button
          type="button"
          disabled={name.trim().length === 0 || saving}
          onClick={() => onSave({
            AccessoryGroupId: isNew ? null : group.AccessoryGroupId,
            Name: name.trim(),
          })}
          className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

